import { supabase } from '../lib/supabase';

export const userService = {
  // Get all users with filtering and pagination
  async getUsers(filters = {}) {
    try {
      let query = supabase?.from('user_profiles')?.select('*');
      
      // Apply filters
      if (filters?.role) {
        query = query?.eq('role', filters?.role);
      }
      
      if (filters?.isActive !== undefined) {
        query = query?.eq('is_active', filters?.isActive);
      }
      
      if (filters?.search) {
        query = query?.or(`full_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`);
      }
      
      // Order by created date
      query = query?.order('created_at', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      return data?.map(user => ({
        id: user?.id,
        fullName: user?.full_name,
        email: user?.email,
        phone: user?.phone,
        timezone: user?.timezone,
        permissions: user?.permissions,
        role: user?.role,
        department: user?.department,
        status: user?.is_active ? 'active' : 'inactive',
        isActive: user?.is_active,
        avatarUrl: user?.avatar_url,
        createdAt: user?.created_at,
        updatedAt: user?.updated_at,
        lastLogin: user?.updated_at // Using updated_at as proxy for last login
      })) || [];
      
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Get user statistics
  async getUserStats() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('role, is_active');
      
      if (error) throw error;
      
      const stats = {
        total: data?.length || 0,
        active: data?.filter(u => u?.is_active === true)?.length || 0,
        inactive: data?.filter(u => u?.is_active === false)?.length || 0,
        pending: 0, // No pending status in current schema
        admin: data?.filter(u => u?.role === 'admin')?.length || 0,
        manager: data?.filter(u => u?.role === 'manager')?.length || 0,
        user: data?.filter(u => u?.role === 'user')?.length || 0
      };
      
      return stats;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  },

  // Create new user - CORRECTED to use proper Supabase Auth
  async createUser(userData) {
    try {
      // Generate a secure temporary password
      const tempPassword = this.generateTempPassword();
      
      // Use Supabase Auth signUp method (proper way)
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: userData?.email,
        password: tempPassword,
        options: {
          data: {
            full_name: userData?.fullName || 'User',
            role: userData?.role || 'user',
            department: userData?.department || '',
            phone: userData?.phone || '',
            timezone: userData?.timezone || 'America/New_York',
            permissions: userData?.permissions || {
              documentRead: true,
              documentWrite: false,
              documentDelete: false,
              userManagement: false,
              systemSettings: false,
              changeManagement: false,
              aiAnalysis: false,
              auditLogs: false
            },
            avatar_url: userData?.avatarUrl || ''
          }
        }
      });

      if (authError) {
        console.error('Auth signup error:', authError);
        // If signup fails, try admin create user (if available)
        return await this.createUserViaAdmin(userData, tempPassword);
      }

      // Wait a moment for trigger to create user_profiles record
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Fetch the created profile
      const { data: profileData, error: profileError } = await supabase?.from('user_profiles')?.select('*')?.eq('email', userData?.email)?.single();

      if (profileError || !profileData) {
        console.error('Profile not found after signup:', profileError);
        // Try to create profile manually if trigger didn't work
        return await this.createProfileForExistingUser(authData?.user?.id, userData);
      }

      // Update profile with any additional data not set by trigger
      if (userData?.department || userData?.role !== 'user' || userData?.phone || userData?.timezone || userData?.permissions) {
        const { data: updatedProfile } = await supabase?.from('user_profiles')?.update({
          role: userData?.role || 'user',
          department: userData?.department,
          phone: userData?.phone,
          timezone: userData?.timezone,
          permissions: userData?.permissions,
          is_active: userData?.isActive !== false
        })?.eq('id', profileData?.id)?.select()?.single();
        
        if (updatedProfile) {
          Object.assign(profileData, updatedProfile);
        }
      }

      return {
        id: profileData?.id,
        fullName: profileData?.full_name,
        email: profileData?.email,
        phone: profileData?.phone,
        timezone: profileData?.timezone,
        permissions: profileData?.permissions,
        role: profileData?.role,
        department: profileData?.department,
        status: profileData?.is_active ? 'active' : 'inactive',
        isActive: profileData?.is_active,
        avatarUrl: profileData?.avatar_url,
        createdAt: profileData?.created_at,
        updatedAt: profileData?.updated_at,
        tempPassword: tempPassword, // Return for admin reference
        note: 'User created successfully. Please share temporary password with user.'
      };

    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  // Admin create user method (fallback)
  async createUserViaAdmin(userData, tempPassword) {
    try {
      // Try using admin API if available
      const { data: adminData, error: adminError } = await supabase?.auth?.admin?.createUser({
        email: userData?.email,
        password: tempPassword,
        email_confirm: true,
        user_metadata: {
          full_name: userData?.fullName || 'User',
          role: userData?.role || 'user',
          department: userData?.department || '',
          phone: userData?.phone || '',
          timezone: userData?.timezone || 'America/New_York',
          permissions: userData?.permissions || {
            documentRead: true,
            documentWrite: false,
            documentDelete: false,
            userManagement: false,
            systemSettings: false,
            changeManagement: false,
            aiAnalysis: false,
            auditLogs: false
          },
          avatar_url: userData?.avatarUrl || ''
        }
      });

      if (adminError) {
        console.error('Admin create user error:', adminError);
        throw new Error(`Failed to create user: ${adminError?.message}`);
      }

      // Wait for trigger to create profile
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Fetch the profile
      const { data: profileData } = await supabase?.from('user_profiles')?.select('*')?.eq('id', adminData?.user?.id)?.single();

      if (!profileData) {
        // Create profile manually
        return await this.createProfileForExistingUser(adminData?.user?.id, userData);
      }

      return {
        id: profileData?.id,
        fullName: profileData?.full_name,
        email: profileData?.email,
        phone: profileData?.phone,
        timezone: profileData?.timezone,
        permissions: profileData?.permissions,
        role: profileData?.role,
        department: profileData?.department,
        status: profileData?.is_active ? 'active' : 'inactive',
        isActive: profileData?.is_active,
        avatarUrl: profileData?.avatar_url,
        createdAt: profileData?.created_at,
        updatedAt: profileData?.updated_at,
        tempPassword: tempPassword
      };

    } catch (error) {
      console.error('Error in admin create user:', error);
      throw error;
    }
  },

  // Create profile for existing auth user
  async createProfileForExistingUser(authUserId, userData) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.insert({
        id: authUserId, // Use the auth.users ID
        email: userData?.email,
        full_name: userData?.fullName || 'User',
        phone: userData?.phone || '',
        timezone: userData?.timezone || 'America/New_York',
        permissions: userData?.permissions || {
          documentRead: true,
          documentWrite: false,
          documentDelete: false,
          userManagement: false,
          systemSettings: false,
          changeManagement: false,
          aiAnalysis: false,
          auditLogs: false
        },
        role: userData?.role || 'user',
        department: userData?.department || '',
        is_active: userData?.isActive !== false,
        avatar_url: userData?.avatarUrl || ''
      })?.select()?.single();

      if (error) {
        console.error('Profile creation error:', error);
        throw error;
      }

      return {
        id: data?.id,
        fullName: data?.full_name,
        email: data?.email,
        phone: data?.phone,
        timezone: data?.timezone,
        permissions: data?.permissions,
        role: data?.role,
        department: data?.department,
        status: data?.is_active ? 'active' : 'inactive',
        isActive: data?.is_active,
        avatarUrl: data?.avatar_url,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };

    } catch (error) {
      console.error('Error creating profile for existing user:', error);
      throw error;
    }
  },

  // Generate secure temporary password
  generateTempPassword() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars?.charAt(Math.floor(Math.random() * chars?.length));
    }
    return password;
  },

  // Update user
  async updateUser(userId, userData) {
    try {
      // Build update object with only provided fields
      const updateData = {};
      
      if (userData?.fullName !== undefined) {
        updateData.full_name = userData?.fullName;
      }
      
      if (userData?.email !== undefined) {
        updateData.email = userData?.email;
      }
      
      if (userData?.phone !== undefined) {
        updateData.phone = userData?.phone;
      }
      
      if (userData?.timezone !== undefined) {
        updateData.timezone = userData?.timezone;
      }
      
      if (userData?.permissions !== undefined) {
        updateData.permissions = userData?.permissions;
      }
      
      if (userData?.role !== undefined) {
        updateData.role = userData?.role;
      }
      
      if (userData?.department !== undefined) {
        updateData.department = userData?.department;
      }
      
      if (userData?.isActive !== undefined) {
        updateData.is_active = userData?.isActive;
      }
      
      if (userData?.avatarUrl !== undefined) {
        updateData.avatar_url = userData?.avatarUrl;
      }

      const { data, error } = await supabase?.from('user_profiles')?.update(updateData)?.eq('id', userId)?.select()?.single();
      
      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }
      
      return {
        id: data?.id,
        fullName: data?.full_name,
        email: data?.email,
        phone: data?.phone,
        timezone: data?.timezone,
        permissions: data?.permissions,
        role: data?.role,
        department: data?.department,
        status: data?.is_active ? 'active' : 'inactive',
        isActive: data?.is_active,
        avatarUrl: data?.avatar_url,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  // Delete users (soft delete by setting inactive)
  async deleteUsers(userIds) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({
        is_active: false
      })?.in('id', userIds);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error('Error deleting users:', error);
      throw error;
    }
  },

  // Bulk activate users
  async activateUsers(userIds) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({
        is_active: true
      })?.in('id', userIds);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error('Error activating users:', error);
      throw error;
    }
  },

  // Bulk deactivate users
  async deactivateUsers(userIds) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({
        is_active: false
      })?.in('id', userIds);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error('Error deactivating users:', error);
      throw error;
    }
  },

  // Reset user password - CLIENT-SIDE SECURE METHOD
  async resetUserPassword(userId, userEmail) {
    try {
      if (!userEmail) {
        throw new Error('User email is required for password reset');
      }

      // Get the configured app URL from environment variable
      // Falls back to window.location.origin if not configured
      const appUrl = import.meta.env?.VITE_APP_URL || window.location?.origin;
      const resetUrl = `${appUrl}/reset-password`;

      // Use client-safe password reset via email
      // This is the ONLY secure way to reset passwords from client-side
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(userEmail, {
        redirectTo: resetUrl
      });

      if (error) {
        console.error('Password reset email error:', error);
        throw error;
      }

      return {
        success: true,
        method: 'email',
        message: 'Password reset email sent successfully. The user will receive an email with instructions to reset their password.'
      };

    } catch (error) {
      console.error('Error resetting user password:', error);
      throw error;
    }
  },

  // Send password reset email (kept for backward compatibility)
  async sendPasswordResetEmail(email) {
    try {
      // Get the configured app URL from environment variable
      const appUrl = import.meta.env?.VITE_APP_URL || window.location?.origin;
      const resetUrl = `${appUrl}/reset-password`;

      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: resetUrl
      });

      if (error) throw error;

      return {
        success: true,
        method: 'email',
        message: 'Password reset email sent successfully. User will receive instructions via email.'
      };

    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    }
  }
};